import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  Layout,
  Typography,
  Input,
  Card,
  Button,
  Tag,
  Row,
  Col,
  Space,
  Tabs,
  Affix,
  Grid,
  FloatButton,
  Badge,
  theme,
  App,
  Modal,
  Calendar,
} from "antd";

import {
  CalendarOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  FireOutlined,
  InfoOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBackendUrl } from "../../utils/env";
import { useAuth } from "../../context/AuthProvider";
const { Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

dayjs.locale("es");

export default function UserEventsScreen() {
  const navigate = useNavigate();
  const { token: themeToken } = theme.useToken();
  const { message } = App.useApp();
  const { authToken, userAuth } = useAuth();
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [eventFilter, setEventFilter] = useState("upcoming");
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const fetchEvents = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${getBackendUrl()}api/events.php`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (res.data.success) {
        const allEvents = res.data.data || [];

        const upcomingFeatured = allEvents
          .filter((event) => {
            const eventDate = dayjs(
              event.event_date,
              "YYYY-MM-DD HH:mm:ss"
            );

            return (
              !eventDate.isBefore(dayjs().startOf("day")) &&
              eventDate.isBefore(
                dayjs().add(30, "day").endOf("day")
              )
            );
          })
          .sort((a, b) =>
            dayjs(a.event_date).unix() -
            dayjs(b.event_date).unix()
          )
          .slice(0, 3);

        setEvents(allEvents);
        setFeaturedEvents(upcomingFeatured);
      } else {
        message.error(res.data.message || "No se pudieron cargar eventos");
      }
    } catch (err) {
      console.error(err);
      message.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const userId =
        localStorage.getItem("user_id") ||
        sessionStorage.getItem("user_id");

      const tkn =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      if (!userId || !tkn) {
        navigate("/");
        return;
      }

      const res = await axios.get(
        `${getBackendUrl()}api/user.php?id=${userId}&fallback_token=${tkn}`,
        {
          headers: {
            Authorization: `Bearer ${tkn}`,
          },
          timeout: 8000,
        }
      );

      let data = res.data;

      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      if (data.success) {

        setUser(data.user);

      } else {
        message.error(
          data.message || "Error al obtener perfil"
        );
      }
    } catch (err) {
      console.error(err);
      message.error("Error al cargar usuario");
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);

        await Promise.all([
          fetchEvents(),
          fetchUser()
        ]);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);


  const getEventImage = (event) => {
    const category =
      event.category_name?.toLowerCase() || "";

    if (category.includes("evento decanal") || category.includes("evento diocesano")) {
      return "https://images.unsplash.com/photo-1563902341721-029085ad9347?q=80&w=1170&auto=format&fit=crop";
    }

    if (category.includes("retiro parroquial")) {
      return "https://plus.unsplash.com/premium_photo-1733317226940-b7b0fe585b83?q=80&w=1171&auto=format&fit=crop";
    }

    if (category.includes("retiro de grupo")) {
      return "https://images.unsplash.com/photo-1662151808629-029b89ce5339?q=80&w=687&auto=format&fit=crop";
    }

    if (category.includes("venta de grupo")) {
      return "https://images.unsplash.com/photo-1518458028785-8fbcd101ebb9?q=80&w=1170&auto=format&fit=crop";
    }

    if (category.includes("hora santa")) {
      return "https://images.unsplash.com/photo-1574786790595-94c66b8740fd?q=80&w=687&auto=format&fit=crop";
    }

    if (category.includes("rosario")) {
      return "https://images.unsplash.com/photo-1585860101517-767d8a345bdb?q=80&w=1171&auto=format&fit=crop";
    }

    if (category.includes("formación")) {
      return "https://plus.unsplash.com/premium_photo-1663099990889-10a861e6aa96?q=80&w=1170&auto=format&fit=crop";
    }

    if (category.includes("limpieza")) {
      return "https://plus.unsplash.com/premium_photo-1679920025550-75324e59680f?q=80&w=1169&auto=format&fit=crop";
    }

    if (category.includes("posada parroquial")) {
      return "https://plus.unsplash.com/premium_photo-1734014585157-805c1815bbb3?q=80&w=1170&auto=format&fit=crop";
    }

    if (category.includes("viacrucis")) {
      return "https://images.unsplash.com/photo-1555892727-55b51e5fceae?q=80&w=1074&auto=format&fit=crop";
    }

    if (category.includes("convivencia parroquial")) {
      return "https://images.unsplash.com/photo-1569292567773-229e2b7521ee?q=80&w=689&auto=format&fit=crop";
    }

    if (category.includes("convivencia grupal")) {
      return "https://plus.unsplash.com/premium_photo-1661719880750-4c0de579cd09?q=80&w=1170&auto=format&fit=crop";
    }

    if (category.includes("posada de grupo")) {
      return "https://plus.unsplash.com/premium_photo-1700554044147-a67dc0f60e35?q=80&w=1170&auto=format&fit=crop&";
    }

    if (category.includes("procesión")) {
      return "https://images.unsplash.com/photo-1739200205946-4c22f850eef8?q=80&w=687&auto=format&fit=crop";
    }

    if (category.includes("misa")) {
      return "https://images.unsplash.com/photo-1576027200053-25e764f9e769?q=80&w=1113&auto=format&fit=crop";
    }

    if (category.includes("periódico mural")) {
      return "https://images.unsplash.com/photo-1741636174186-3fffe1614c39?q=80&w=1083&auto=format&fit=crop";
    }

    if (category.includes("venta de postres")) {
      return "https://plus.unsplash.com/premium_photo-1681826507324-0b3c43928753?q=80&w=1169&auto=format&fit=crop";
    }

    if (category.includes("venta de antojitos")) {
      return "https://images.unsplash.com/photo-1676081986290-ac79c2968c3f?q=80&w=880&auto=format&fit=crop&";
    }

    if (category.includes("decoración")) {
      return "https://images.unsplash.com/photo-1638687095221-0c81b6d897ab?q=80&w=687&auto=format&fit=crop";
    }

    if (category.includes("feria de las áreas")) {
      return "https://plus.unsplash.com/premium_photo-1714347050918-78c0f8f35a5f?q=80&w=1170&auto=format&fit=crop";
    }

    return "https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=1200&auto=format&fit=crop";
  };


 const formatEventDate = (event) => {
  const eventDate = dayjs(event.event_date);

  const isAllDay =
    event.all_day ||
    eventDate.format("HH:mm:ss") === "00:00:00";

  if (isAllDay) {
    if (eventDate.isSame(dayjs(), "day")) {
      return "Hoy • Todo el día";
    }

    if (eventDate.isSame(dayjs().add(1, "day"), "day")) {
      return "Mañana • Todo el día";
    }

    return `Todo el día • ${eventDate.format(
      "dddd D [de] MMMM"
    )}`;
  }

  if (eventDate.isSame(dayjs(), "day")) {
    return `Hoy • ${eventDate.format("h:mm A")}`;
  }

  if (eventDate.isSame(dayjs().add(1, "day"), "day")) {
    return `Mañana • ${eventDate.format("h:mm A")}`;
  }

  return eventDate.format(
    "dddd D [de] MMMM • h:mm A"
  );
};

  const exportToCalendar = (record) => {
    const eventTitle = record.event_name;
    const eventLocation = record.location || "Sin ubicación";
    const eventDescription = record.description || "";

    // 1. Crear el objeto Date inicial a partir de la fecha de la base de datos
    // Reemplazamos el espacio por 'T' para asegurar compatibilidad con el constructor de Date
    const startDateObj = new Date(record.event_date.replace(" ", "T"));

    // 2. Calcular la fecha de fin sumándole EXACTAMENTE 2 horas de manera segura
    const endDateObj = new Date(startDateObj.getTime() + (2 * 60 * 60 * 1000));

    // Función interna para formatear la fecha al estándar estricto de iCal (YYYYMMDDTHHMMSS)
    // Nota: No agregamos la 'Z' al final para que actúe como "Hora Local Estática" en el celular
    const formatToiCalDate = (date) => {
      const pad = (num) => String(num).padStart(2, '0');
      return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
        'T',
        pad(date.getHours()),
        pad(date.getMinutes()),
        pad(date.getSeconds())
      ].join('');
    };

    const dateStart = formatToiCalDate(startDateObj);
    const dateEnd = formatToiCalDate(endDateObj);

    // 3. Construir la estructura limpia del estándar iCalendar
    const icsStructure = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Sentinel//Eventos//ES",
      "CALSCALE:GREGORIAN",
      "BEGIN:VEVENT",
      `UID:${record.id}-${Date.now()}@sentinel.com`,
      `DTSTAMP:${formatToiCalDate(new Date())}`,
      `DTSTART:${dateStart}`,
      `DTEND:${dateEnd}`,
      `SUMMARY:${eventTitle}`,
      `DESCRIPTION:${eventDescription}`,
      `LOCATION:${eventLocation}`,
      "END:VEVENT",
      "END:VCALENDAR"
    ].join("\r\n");

    // 4. Crear el archivo en memoria y forzar la descarga
    const blob = new Blob([icsStructure], { type: "text/calendar;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${eventTitle.replace(/\s+/g, "_")}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };



const filteredEvents = events
  .filter((event) => {
    const query = search.toLowerCase();

    const matchesSearch =
      event.event_name?.toLowerCase().includes(query) ||
      event.location?.toLowerCase().includes(query) ||
      event.description?.toLowerCase().includes(query);

    const eventDate = dayjs(event.event_date);

    const matchesDate =
      eventFilter === "upcoming"
        ? !eventDate.isBefore(dayjs().startOf("day"))
        : eventDate.isBefore(dayjs().startOf("day"));

    return matchesSearch && matchesDate;
  })
  .sort((a, b) => {
    if (eventFilter === "upcoming") {
      return dayjs(a.event_date).unix() - dayjs(b.event_date).unix();
    }

    return dayjs(b.event_date).unix() - dayjs(a.event_date).unix();
  });


  const getListData = (value) => {
    return events.filter((event) =>
      dayjs(event.event_date).isSame(value, "day")
    );
  };


  const dateCellRender = (value) => {
    const listData = getListData(value);

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          // Evita que el contenedor empuje las celdas vecinas hacia la derecha
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        {listData.slice(0, isMobile ? 1 : 2).map((item) => {
          // En móvil cortamos a 5 caracteres, en desktop dejamos el título completo
          const textToShow = isMobile
            ? (item.event_name?.length > 5 ? `${item.event_name.substring(0, 5)}..` : item.event_name)
            : item.event_name;

          return (
            <div
              key={item.id}
              title={item.event_name} // Muestra el nombre completo al dejar el dedo/mouse presionado
              style={{
                fontSize: isMobile ? 8 : 11,
                lineHeight: isMobile ? "11px" : "14px",
                padding: isMobile ? "1px 3px" : "2px 6px",
                borderRadius: 3,
                background: themeToken.colorPrimaryBg,
                color: themeToken.colorPrimary,
                // Reglas críticas para congelar el desborde horizontal:
                display: "block",
                width: "100%",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box",
                textAlign: "center",
              }}
            >
              {textToShow}
            </div>
          );
        })}

        {/* Si hay más de un evento en móvil, avisamos de forma muy sutil */}
        {listData.length > (isMobile ? 1 : 2) && (
          <span
            style={{
              fontSize: 8,
              color: themeToken.colorTextSecondary,
              display: "block",
              textAlign: "center",
              lineHeight: "8px",
            }}
          >
            +{listData.length - (isMobile ? 1 : 2)}
          </span>
        )}
      </div>
    );
  };
  return (
    <Layout style={{ minHeight: "100vh", background: themeToken.colorBgLayout }}>
      <Affix offsetTop={0}>
        <div
          style={{
            background: themeToken.colorBgContainer,
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${themeToken.colorBorder}`,
            padding: isMobile ? "16px" : "20px 32px",
            zIndex: 20,
          }}
        >
          <Space
            orientation="vertical"
            size={16}
            style={{ width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                Hola, {user?.name || "Usuario"} 👋

                <Title
                  level={isMobile ? 3 : 2}
                  style={{ margin: 0 }}
                >
                  Próximos Eventos
                </Title>
              </div>

              {!isMobile && (
                <Button
                  type="primary"
                  size="large"
                  icon={<CalendarOutlined />}
                  onClick={() => setCalendarOpen(true)}
                  style={{
                    borderRadius: 14,
                    height: 48,
                    paddingInline: 24,
                    fontWeight: 600,
                  }}
                >
                  Ver Calendario
                </Button>
              )}
            </div>

            <Input
              size="large"
              prefix={<SearchOutlined />}
              placeholder="Buscar eventos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                borderRadius: 16,
                height: 50,
              }}
            />
          </Space>
        </div>
      </Affix>

      <Content
        style={{
          padding: isMobile ? 16 : 32,
          maxWidth: 1400,
          width: "100%",
          margin: "0 auto",
        }}
      >
        <Modal
          open={calendarOpen}
          onCancel={() => setCalendarOpen(false)}
          footer={null}
          title="Calendario de Eventos"
          width={isMobile ? "100%" : 1000}
          style={{
            top: isMobile ? 0 : 20,
            margintop: isMobile ? 0 : 100,
          }}
          styles={{
            body: {
              padding: isMobile ? 12 : 24,
            },
          }}
        >
          <Calendar
            fullscreen={!isMobile}
            cellRender={(current, info) => {
              // Solo renderizamos los eventos si estamos en la vista de "fecha" (mes)
              if (info.type === 'date') return dateCellRender(current);
              return info.originNode;
            }}
          />
        </Modal>

        <Space
          orientation="vertical"
          size={32}
          style={{ width: "100%" }}
        >
          <div>
            <Space
              align="center"
              style={{ marginBottom: 16, width: "100%" }}
            >
              <FireOutlined style={{ color: "#fa8c16" }} />

              {/* Convertimos el Title en un contenedor Flex para empujar el Badge a la derecha */}
              <Title
                level={4}
                style={{
                  margin: 0,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%"
                }}
              >
                <span>Eventos Destacados</span>
                <Badge count={featuredEvents.length} color="#fa8c16" />
              </Title>

            </Space>

            <Row gutter={[20, 20]}>
              {featuredEvents.map((event) => (
                <Col xs={24} lg={12} key={event.id}>
                  <Card
                    hoverable
                    cover={
                      <div
                        style={{
                          position: "relative",
                          height: isMobile ? 220 : 320,
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={getEventImage(event)}
                          alt={event.event_name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />

                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                          }}
                        />

                        <div
                          style={{
                            position: "absolute",
                            bottom: 20,
                            left: 20,
                            right: 20,
                            color: "white",
                          }}
                        >
                          <Tag color="gold">Próximo Evento</Tag>

                          <Title
                            level={isMobile ? 4 : 2}
                            style={{
                              color: "white",
                              marginTop: 12,
                              marginBottom: 0,
                            }}
                          >
                            {event.title}
                          </Title>
                        </div>
                      </div>
                    }
                    style={{
                      borderRadius: 24,
                      overflow: "hidden",
                      border: "none",
                      boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
                    }}
                    bodyStyle={{ padding: 24 }}
                  >
                    <Space orientation="vertical" size={14}>
                      <Space>
                        <InfoOutlined />
                        <Text type="success">
                          {event.event_name.length > 100
                            ? event.event_name.substring(0, 100) + "..."
                            : event.event_name}
                        </Text>


                      </Space>
                      <Space>
                        <CalendarOutlined />

                        <Text>{formatEventDate(event)}</Text>

                      </Space>


                      <Space>
                        <EnvironmentOutlined />
                        <Text>{event.location}</Text>
                      </Space>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 8,
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >


                        <Button
                          type="primary"
                          icon={<CalendarOutlined />}
                          style={{ borderRadius: 12 }}
                          onClick={() => exportToCalendar(event)}
                        >
                          Añadir a Calendario
                        </Button>
                      </div>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>

          </div>

          <Tabs
            activeKey={eventFilter}
            onChange={setEventFilter}
            items={[
              {
                key: "upcoming",
                label: "Próximos",
              },
              {
                key: "past",
                label: "Pasados",
              },
            ]}
          />

          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 20,
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  Todos los eventos
                </Title>

                <Text type="secondary">
                  Descubre actividades de la comunidad
                </Text>
              </div>

              <Badge count={filteredEvents.length} color="#1677ff" />
            </div>

            <Row gutter={[20, 20]}>
              {filteredEvents.map((event) => (
                <Col xs={24} sm={12} xl={8} key={event.id}>
                  <Card
                    hoverable
                    style={{
                      borderRadius: 22,
                      overflow: "hidden",
                      border: "none",
                      height: "100%",
                      boxShadow: "0 6px 24px rgba(0,0,0,0.05)",
                    }}
                    cover={
                      <div
                        style={{
                          height: 220,
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        <img
                          src={getEventImage(event)}
                          alt={event.event_name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />

                        <Tag
                          color="blue"
                          style={{
                            position: "absolute",
                            top: 16,
                            left: 16,
                            borderRadius: 999,
                            paddingInline: 12,
                          }}
                        >
                          {event.category_name}
                        </Tag>
                      </div>
                    }
                  >
                    <Space
                      orientation="vertical"
                      size={14}
                      style={{ width: "100%", padding: 22 }}
                    >
                      <Title level={5} style={{ margin: 0 }}>
                        {event.title}
                      </Title>

                      <Space orientation="vertical" size={10}>
                        <Space>
                          <InfoOutlined />
                          <Text type="success">
                            {event.event_name.length > 100
                              ? event.event_name.substring(0, 100) + "..."
                              : event.event_name}
                          </Text>
                        </Space>
                        <Space>
                          <CalendarOutlined />
                          <Text>{formatEventDate(event)}</Text>
                        </Space>


                        <Space align="start">
                          <EnvironmentOutlined />
                          <Text>{event.location}</Text>
                        </Space>
                      </Space>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: 8,
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <Space>
                          <Button
                            icon={<CalendarOutlined />}
                            style={{ borderRadius: 12 }}
                            disabled={eventFilter === "past"}
                            onClick={() => exportToCalendar(event)}
                          >
                            {eventFilter === "past"
                              ? "Evento Finalizado"
                              : "Añadir a Calendario"}
                          </Button>
                        </Space>
                      </div>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Space>
      </Content>

      {isMobile && (
        <FloatButton
          icon={<CalendarOutlined />}
          type="primary"
          onClick={() => setCalendarOpen(true)}


        />
      )}
    </Layout>
  );
}
